/**
 * Product Tree Utilities
 * Helpers for building product navigation stacks from product relationships
 */

import type { ProductSummary } from '@cabal/shared/domain/session/product/types.js';

/**
 * Result of building a product stack from a URL viewing param
 */
export interface ProductStackFromUrlResult {
  /** The product ID that should be focused (null if not found) */
  focusedProductId: string | null;
  /** The product stack (parents from root to immediate parent, excluding focused) */
  productStack: string[];
}

/**
 * Builds a product navigation stack from a product id.
 *
 * The product stack is the focused product's ancestor chain, root first,
 * excluding the focused product itself.
 */
export function buildProductStackFromProductId(
  products: Record<string, ProductSummary>,
  productId: string,
  rootProductId: string | null
): ProductStackFromUrlResult {
  const targetProduct = products[productId];
  if (!targetProduct) {
    return {
      focusedProductId: null,
      productStack: [],
    };
  }

  if (targetProduct.id === rootProductId) {
    return {
      focusedProductId: targetProduct.id,
      productStack: [],
    };
  }

  const tempStack: string[] = [];
  const visited = new Set<string>([targetProduct.id]);
  let currentProduct = targetProduct;
  let reachedRoot = false;

  while (currentProduct.parentProductId) {
    const parentProduct = products[currentProduct.parentProductId];
    if (!parentProduct) {
      return {
        focusedProductId: null,
        productStack: [],
      };
    }
    if (visited.has(parentProduct.id)) {
      return {
        focusedProductId: null,
        productStack: [],
      };
    }
    visited.add(parentProduct.id);
    tempStack.push(parentProduct.id);
    if (parentProduct.id === rootProductId) {
      reachedRoot = true;
      break;
    }
    currentProduct = parentProduct;
  }

  if (rootProductId !== null && !reachedRoot) {
    return {
      focusedProductId: null,
      productStack: [],
    };
  }

  return {
    focusedProductId: targetProduct.id,
    productStack: tempStack.reverse(),
  };
}

/**
 * Builds a product navigation stack from a URL viewing parameter.
 *
 * The viewing param is a canonical product short-id (e.g. `p3`). This
 * function:
 * 1. Finds the product whose `shortId.canonical` matches the param
 * 2. Traverses up to the root building the parent chain
 * 3. Returns the stack in correct order (root first, immediate parent last)
 */
export function buildProductStackFromUrl(
  products: Record<string, ProductSummary>,
  viewingShortId: string,
  rootProductId: string | null
): ProductStackFromUrlResult {
  const trimmedShortId = viewingShortId.trim();
  if (!trimmedShortId) {
    return {
      focusedProductId: null,
      productStack: [],
    };
  }

  // Find the product whose canonical short-id matches the URL param.
  const targetProduct = Object.values(products).find(
    product => product.shortId.canonical === trimmedShortId
  );

  if (!targetProduct) {
    return {
      focusedProductId: null,
      productStack: [],
    };
  }

  return buildProductStackFromProductId(products, targetProduct.id, rootProductId);
}
